const QuantileCount = 20;

class Resumer {
    constructor() {
        this._promise = null;
        this._signal = null;
    }

    wait() {
        if (!this._promise) {
            this._promise = new Promise((resolve, reject) => {
                this._signal = resolve;
            });
        }

        return this._promise;
    }

    trigger(execTime) {
        if (this._promise) {
            let signal = this._signal;
            this._promise = null;
            this._signal = null;
            signal(Date.now()+execTime);
        }
    }
}

function setPop(set) {
    let value;
    let exists = false;

    for (let thisValue of set) {
        value = thisValue;
        exists = true;
        break;
    }

    if (exists) {
        set.delete(value);
        return value;
    }
}

class FaceSorter {
    constructor() {
        this._queue = new Set();
        this._currentEntry = null;

        this._resumer = new Resumer();
    }

    addGeometry(geometry) {
        let entry = { geometry };

        let promise = new Promise((resolve, reject) => {
            entry.resolve = resolve;
            entry.reject = reject;
        });

        this._queue.add(entry);
        this._updateQueue();

        return promise;
    }

    _updateQueue() {
        if (this._currentEntry) {
            return;
        }

        let entry = setPop(this._queue);

        if (!entry) {
            return;
        }

        entry.execPromise = sortGeometryFaces(entry.geometry, this._resumer);
        this._currentEntry = entry;

        entry.execPromise
            .then(() => entry.resolve(), () => entry.reject())
            .finally(() => {
                this._currentEntry = null;
                this._updateQueue();
            });
    }

    resumeLoading(execTime) {
        this._resumer.trigger(execTime);
    }
}

const pos0 = new THREE.Vector3();
const pos1 = new THREE.Vector3();
const pos2 = new THREE.Vector3();
const pos = [pos0, pos1, pos2];
const normal = new THREE.Vector3();

async function sortGeometryFaces(geometry, resumer) {
    if (!(geometry instanceof THREE.BufferGeometry)) {
        console.warn('sortGeometryFaces: only THREE.BufferGeometry is supported');
        return;
    }



    let yieldTime = await resumer.wait();



    if (!geometry.boundingSphere) {
        geometry.computeBoundingSphere();
    }

    const { faceCount, faceDistances, faceIndices, faceMaterials, yieldTime: yieldTime1 } = await getGeometryFaceInfo(geometry, resumer, yieldTime);
    yieldTime = yieldTime1;



    const preciseFaceOrder = new Uint32Array(faceCount);

    for (let faceIndex = 0; faceIndex < faceCount; faceIndex++) {
        preciseFaceOrder[faceIndex] = faceIndex;

        if (faceIndex%100 === 0 && Date.now() >= yieldTime) {
            yieldTime = await resumer.wait();
        }
    }

    preciseFaceOrder.sort((i1, i2) => faceDistances[i1]-faceDistances[i2]);



    let lastMat = null;
    const faceOrder = new Uint32Array(faceCount);
    let finalFaceIndex = 0;

    for (let qNum = 0; qNum < QuantileCount; qNum++) {
        const faceStart = Math.round(faceCount*qNum/QuantileCount);
        const faceEnd = Math.round(faceCount*(qNum+1)/QuantileCount);

        const materialFaces = new Map();
        const matFacePieces = [];

        for (let faceNum = faceStart; faceNum < faceEnd; faceNum++) {
            const faceIndex = preciseFaceOrder[faceNum];
            const material = faceMaterials[faceIndex];

            let thisMatFaces = materialFaces.get(material);
            if (!thisMatFaces) {
                thisMatFaces = [];
                materialFaces.set(material, thisMatFaces);

                if (material === lastMat) {
                    matFacePieces.unshift(thisMatFaces);
                } else {
                    matFacePieces.push(thisMatFaces);
                }
            }

            thisMatFaces.push(faceIndex);

            if (faceNum%100 === 0 && Date.now() >= yieldTime) {
                yieldTime = await resumer.wait();
            }
        }

        for (let thisMatFaces of matFacePieces) {
            for (let faceIndex of thisMatFaces) {
                faceOrder[finalFaceIndex++] = faceIndex;

                if (finalFaceIndex%100 === 0 && Date.now() >= yieldTime) {
                    yieldTime = await resumer.wait();
                }
            }

            let faceIndex = thisMatFaces[0];
            lastMat = faceMaterials[faceIndex];
        }
    }



    if (!geometry.getIndex()) {
        let indices = new Uint32Array(faceOrder*3);
        let indexAttrib = new THREE.BufferAttribute(indices, 1, false)
        geometry.setIndex(indexAttrib);
    }

    rebuildGeometryIndices(geometry, faceOrder, faceIndices, faceMaterials);
}

function getGeometryGroups(geometry) {
    if (geometry.groups) {
        return geometry.groups;
    } else {
        let count;

        let indexAttrib = geometry.getIndex();
        if (indexAttrib) {
            count = indexAttrib.count;
        } else {
            count = geometry.getAttribute('position').count;
        }

        return [{ start: 0, count, materialIndex: 0 }];
    }
}

function getGeometryIndices(geometry) {
    const indexAttrib = geometry.getIndex();

    if (indexAttrib) {
        return indexAttrib.array;
    } else {
        let count = geometry.getAttribute('position').count;
        let indices = [];
        for (let index = 0; index < count; index++) {
            indices[index] = index;
        }
        return indices;
    }
}

async function getGeometryFaceInfo(geometry, resumer, yieldTime) {
    const groups = getGeometryGroups(geometry);

    let faceCount = 0;
    for (let { count } of groups) {
        faceCount += Math.floor(count/3);
    }

    const indices = getGeometryIndices(geometry);
    const positions = geometry.getAttribute('position').array;

    const faceDistances = new Float32Array(faceCount);
    const faceIndices = new Uint32Array(faceCount*3);
    const faceMaterials = new Uint32Array(faceCount);

    const center = geometry.boundingSphere.center;

    let faceIndex = 0;
    for (let { start, count, materialIndex } of groups) {
        let end = start+count;
        for (let indexNum0 = start; indexNum0 < end; indexNum0 += 3) {
            for (let faceVertexNum = 0; faceVertexNum < 3; faceVertexNum++) {
                let indexNum = indexNum0+faceVertexNum;
                let vertexIndex = indices[indexNum];
                let vertexPosOffset = vertexIndex*3;
                let thisPos = pos[faceVertexNum];
                thisPos.set(positions[vertexPosOffset+0], positions[vertexPosOffset+1], positions[vertexPosOffset+2]);

                faceIndices[faceIndex*3+faceVertexNum] = vertexIndex;
            }

            pos1.sub(pos0);
            pos2.sub(pos0);
            pos0.sub(center);
            normal.crossVectors(pos1, pos2).normalize();

            faceDistances[faceIndex] = normal.dot(pos0);
            faceMaterials[faceIndex] = materialIndex;

            if (faceIndex%100 === 0 && Date.now() >= yieldTime) {
                yieldTime = await resumer.wait();
            }

            faceIndex++;
        }
    }

    return { faceCount, faceDistances, faceIndices, faceMaterials, yieldTime };
}

function rebuildGeometryIndices(geometry, faceOrder, faceIndices, faceMaterials) {
    const indexAttrib = geometry.getIndex();
    const geoIndices = indexAttrib.array;

    if (geometry.groups) {
        geometry.clearGroups();

        let groupStart = null, groupMaterial = null;

        for (let [faceNumber, faceIndex] of faceOrder.entries()) {
            for (let i = 0; i < 3; i++) {
                geoIndices[faceNumber*3+i] = faceIndices[faceIndex*3+i];
            }

            let material = faceMaterials[faceIndex];

            if (material !== groupMaterial) {
                if (groupStart !== null) {
                    geometry.addGroup(groupStart*3, (faceNumber-groupStart)*3, groupMaterial);
                }

                groupStart = faceNumber;
                groupMaterial = material;
            }
        }

        geometry.addGroup(groupStart*3, (faceOrder.length-groupStart)*3, groupMaterial);
    } else {
        for (let [faceNumber, faceIndex] of faceOrder.entries()) {
            for (let i = 0; i < 3; i++) {
                geoIndices[faceNumber*3+i] = faceIndices[faceIndex*3+i];
            }
        }
    }

    indexAttrib.needsUpdate = true;
}

export default new FaceSorter();
